import { useMemo, useEffect } from 'react';
import type { KitResponse, SelectedProduct, FitPreference } from '@nfa/types';
import { ProductCard } from './ProductCard';
import { ProductCardCompact } from './ProductCardCompact';
import { WeatherPanel } from './WeatherPanel';
import { WeightChart } from './WeightChart';
import { ExportShare } from './ExportShare';
import { CurrencyToggle } from './CurrencyToggle';
import { ViewToggle } from './ViewToggle';
import { SelectionContextPanel } from './SelectionContextPanel';
import { KitConversions, type ConversionOptions } from './KitConversions';
import { useOwnedItems } from '../hooks/useOwnedItems';
import { useCurrency } from '../hooks/useCurrency';
import { useViewMode } from '../hooks/useViewMode';
import { trackKitResult } from '../lib/analytics';

interface KitResultsProps {
  data: KitResponse;
  onNewSearch: () => void;
  onSwapProduct?: (categoryId: string, productId: string) => void;
  onConvert?: (options: ConversionOptions) => void;
  isConverting?: boolean;
  convertingTo?: ConversionOptions;
  currentFit?: FitPreference;
}

function groupByCategory(items: SelectedProduct[]): Map<string, SelectedProduct[]> {
  const grouped = new Map<string, SelectedProduct[]>();
  
  for (const item of items) {
    const category = item.category_name;
    if (!grouped.has(category)) {
      grouped.set(category, []);
    }
    grouped.get(category)!.push(item);
  }
  
  return grouped;
}

function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

/**
 * Format a location string for display
 * Converts snake_case to Title Case (e.g., "tour_du_mont_blanc" → "Tour du Mont Blanc")
 */
function formatLocation(location: string | null): string | null {
  if (!location) return null;

  return location
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function KitResults({
  data,
  onNewSearch,
  onSwapProduct,
  onConvert,
  isConverting = false,
  convertingTo,
  currentFit = 'unisex',
}: KitResultsProps) {
  const { inference, kit, meta } = data;

  // Determine current modifier from inference
  const currentModifier = useMemo(() => {
    if (inference.style === 'ultralight') return 'ultralight';
    if (inference.budget_tier === 'budget') return 'budget';
    if (inference.budget_tier === 'premium') return 'premium';
    return undefined;
  }, [inference.style, inference.budget_tier]);
  const { isOwned, toggleOwned } = useOwnedItems();
  const { currency, setCurrency, formatPrice } = useCurrency();
  const { mode: viewMode, setViewMode } = useViewMode();

  // Split items into owned and need-to-buy
  const { needToBuy, alreadyOwn } = useMemo(() => {
    const need: SelectedProduct[] = [];
    const own: SelectedProduct[] = [];
    for (const item of kit.items) {
      if (isOwned(item.product_id)) {
        own.push(item);
      } else {
        need.push(item);
      }
    }
    return { needToBuy: need, alreadyOwn: own };
  }, [kit.items, isOwned]);

  const groupedNeedToBuy = groupByCategory(needToBuy);
  const groupedAlreadyOwn = groupByCategory(alreadyOwn);

  // Calculate adjusted totals (only items to purchase)
  const purchaseTotals = useMemo(() => {
    const price = needToBuy.reduce((sum, item) => sum + item.price_gbp, 0);
    const weight = needToBuy.reduce((sum, item) => sum + item.weight_g, 0);
    return {
      price_gbp: price,
      weight_kg: weight / 1000,
      item_count: needToBuy.length,
    };
  }, [needToBuy]);

  const locationDisplay = formatLocation(inference.location.specific_route) ||
    formatLocation(inference.location.region) ||
    inference.location.country.charAt(0).toUpperCase() + inference.location.country.slice(1);

  // Track kit result view for analytics
  useEffect(() => {
    trackKitResult(locationDisplay, kit.totals.item_count, kit.totals.price_gbp);
  }, [locationDisplay, kit.totals.item_count, kit.totals.price_gbp]);

  return (
    <div className="w-full max-w-6xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={onNewSearch}
          className="flex items-center gap-1.5 text-neutral-500 hover:text-white text-sm transition-colors mb-6"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          <span>New search</span>
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">
          <div>
            <h2 className="font-mono text-2xl sm:text-3xl font-bold text-white mb-2">
              Your Kit for {locationDisplay}
            </h2>
            <p className="text-neutral-400">
              {inference.timing.month} · {inference.duration_days} days · {formatLocation(inference.activity)} · {formatLocation(inference.accommodation)}
            </p>
          </div>

          {/* View & Currency Toggles */}
          <div className="flex items-center gap-3 shrink-0">
            <ViewToggle mode={viewMode} onChange={setViewMode} />
            <CurrencyToggle current={currency} onChange={setCurrency} />
          </div>
        </div>
      </div>

      {/* Kit Conversions - Quick style/fit switches */}
      {onConvert && (
        <div className="mb-8">
          <KitConversions
            query={data.inference.location.specific_route || data.inference.location.region || ''}
            currentModifier={currentModifier}
            currentFit={currentFit}
            currentTotals={{
              price_gbp: kit.totals.price_gbp,
              weight_g: kit.totals.weight_g,
            }}
            onConvert={onConvert}
            isConverting={isConverting}
            convertingTo={convertingTo}
          />
        </div>
      )}

      {/* Weather Panel */}
      <div className="mb-8">
        <WeatherPanel weather={meta.weather} inference={inference} />
      </div>

      {/* Selection Context - Personalized "Why We Picked This" */}
      {inference.selection_context && (
        <SelectionContextPanel context={inference.selection_context} />
      )}

      {/* Weight Chart */}
      <div className="mb-8">
        <WeightChart items={kit.items} />
      </div>

      {/* Products to Buy */}
      {needToBuy.length > 0 && (
        <div className="space-y-8">
          {alreadyOwn.length > 0 && (
            <h2 className="font-mono text-xl font-bold text-white flex items-center gap-3">
              <span className="text-orange-500">Need to Buy</span>
              <span className="text-sm font-normal text-neutral-500">
                {needToBuy.length} items · {formatPrice(purchaseTotals.price_gbp)}
              </span>
            </h2>
          )}
          {Array.from(groupedNeedToBuy.entries()).map(([category, products]) => (
            <section key={category}>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-mono text-lg font-semibold text-white uppercase tracking-wider">
                  {category}
                </h3>
                <div className="h-px flex-1 bg-neutral-800" />
                <span className="text-sm text-neutral-500">
                  {products.length} item{products.length > 1 ? 's' : ''} · {formatPrice(products.reduce((sum, p) => sum + p.price_gbp, 0))} · {formatWeight(products.reduce((sum, p) => sum + p.weight_g, 0))}
                </span>
              </div>
              {viewMode === 'grid' ? (
                <div data-testid="product-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <ProductCard
                      key={product.product_id}
                      product={product}
                      onSwap={onSwapProduct}
                      onToggleOwned={toggleOwned}
                      isOwned={false}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              ) : (
                <div data-testid="product-list" className="flex flex-col gap-2">
                  {products.map((product) => (
                    <ProductCardCompact
                      key={product.product_id}
                      product={product}
                      onSwap={onSwapProduct}
                      onToggleOwned={toggleOwned}
                      isOwned={false}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Already Owned Items */}
      {alreadyOwn.length > 0 && (
        <div className="space-y-8 mt-10 pt-8 border-t border-neutral-800">
          <h2 className="font-mono text-xl font-bold text-white flex items-center gap-3">
            <span className="text-green-400">Already Own</span>
            <span className="text-sm font-normal text-neutral-500">
              {alreadyOwn.length} items · {formatWeight(alreadyOwn.reduce((sum, p) => sum + p.weight_g, 0))}
            </span>
          </h2>
          {Array.from(groupedAlreadyOwn.entries()).map(([category, products]) => (
            <section key={category}>
              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-mono text-lg font-semibold text-white uppercase tracking-wider">
                  {category}
                </h3>
                <div className="h-px flex-1 bg-neutral-800" />
                <span className="text-sm text-neutral-500">
                  {products.length} item{products.length > 1 ? 's' : ''}
                </span>
              </div>
              {viewMode === 'grid' ? (
                <div data-testid="product-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {products.map((product) => (
                    <ProductCard
                      key={product.product_id}
                      product={product}
                      onSwap={onSwapProduct}
                      onToggleOwned={toggleOwned}
                      isOwned={true}
                      showAlternatives={false}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              ) : (
                <div data-testid="product-list" className="flex flex-col gap-2">
                  {products.map((product) => (
                    <ProductCardCompact
                      key={product.product_id}
                      product={product}
                      onSwap={onSwapProduct}
                      onToggleOwned={toggleOwned}
                      isOwned={true}
                      formatPrice={formatPrice}
                    />
                  ))}
                </div>
              )}
            </section>
          ))}
        </div>
      )}

      {/* Kit Summary Totals */}
      <div className="mt-10 pt-8 border-t border-neutral-800">
        <div className="flex flex-wrap gap-4 sm:gap-6 p-5 bg-neutral-900 border border-neutral-800 rounded-xl">
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">
              {alreadyOwn.length > 0 ? 'To Buy' : 'Total'}
            </p>
            <p className="font-mono text-2xl font-bold text-orange-500">
              {formatPrice(purchaseTotals.price_gbp)}
            </p>
            {alreadyOwn.length > 0 && (
              <p className="text-xs text-neutral-500 mt-0.5">
                Full kit: {formatPrice(kit.totals.price_gbp)}
              </p>
            )}
          </div>
          <div className="w-px bg-neutral-700" />
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Kit Weight</p>
            <p className="font-mono text-2xl font-bold text-white">
              {kit.totals.weight_kg.toFixed(1)}kg
            </p>
          </div>
          <div className="w-px bg-neutral-700" />
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Items</p>
            <p className="font-mono text-2xl font-bold text-white">
              {kit.totals.item_count}
            </p>
            {alreadyOwn.length > 0 && (
              <p className="text-xs text-green-400 mt-0.5">
                {alreadyOwn.length} owned
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Export/Share Section */}
      <div className="mt-6">
        <ExportShare
          kit={kit}
          inference={inference}
          weather={meta.weather}
          generatedAt={meta.generated_at}
        />
      </div>

      {/* Footer Note */}
      <p className="mt-8 text-center text-xs text-neutral-600">
        Affiliate links support the site. Picks are never influenced by commission rates.{' '}
        <a href="#how" className="text-neutral-500 hover:text-white underline transition-colors">
          How we pick
        </a>
      </p>
    </div>
  );
}
